// ---------------------------------------------------------------------------
// useGoogleMaps — loader idempotente da Maps JavaScript API (handoff 0030 §5).
//
// Hook compartilhado (SOLID/reuso): consumido pelo board (HFMap,
// LocationPickerModal) e pelo seletor de ponto do endereço (LocationPickModal).
// Sem dependência nova: injeta o <script> da API UMA vez por sessão de página
// (promise cacheada em nível de módulo) e expõe o objeto google.maps já
// carregado. Carrega a library `geometry` — necessária pra decodificar a
// polyline da rota (google.maps.geometry.encoding.decodePath).
//
// Key-ready: lê REACT_APP_GOOGLE_MAPS_API_KEY do .env. SEM chave, o hook
// devolve { apiKey: null, isLoaded: false } e o consumidor cai no fallback
// (mapa SVG no board, campos lat/long no picker de endereço) — nada quebra
// enquanto o OPS não libera a chave de browser.
//
// A chave fica exposta no bundle (normal pra Maps JS): a proteção é a
// restrição por HTTP-referrer + API no GCP, não o sigilo da string.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || null;
const CALLBACK_NAME = '__myshopGmapsReady';
const SCRIPT_ID = 'myshop-gmaps-js';

// Promise única compartilhada por todos os mounts. Resolve com window.google
// quando a API termina de carregar; rejeita em erro de rede/chave inválida.
let loaderPromise = null;

function loadGoogleMaps() {
   if (loaderPromise) return loaderPromise;

   if (!API_KEY) {
      loaderPromise = Promise.reject(new Error('no-api-key'));
      return loaderPromise;
   }

   loaderPromise = new Promise((resolve, reject) => {
      // Já carregado por outra rota/HMR — reaproveita.
      if (window.google && window.google.maps) {
         resolve(window.google);
         return;
      }

      const existing = document.getElementById(SCRIPT_ID);
      if (existing) {
         existing.addEventListener('load', () => resolve(window.google));
         existing.addEventListener('error', () =>
            reject(new Error('gmaps-script-error'))
         );
         return;
      }

      // Callback global exigido por loading=async (evita o warning do console
      // e garante que a API está 100% pronta antes do resolve).
      window[CALLBACK_NAME] = () => {
         resolve(window.google);
         delete window[CALLBACK_NAME];
      };

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      const params = new URLSearchParams({
         key: API_KEY,
         libraries: 'geometry',
         v: 'weekly',
         loading: 'async',
         callback: CALLBACK_NAME,
      });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.onerror = () => reject(new Error('gmaps-script-error'));
      document.head.appendChild(script);
   });

   return loaderPromise;
}

// Hook: { apiKey, isLoaded, loadError }.
//   apiKey null      → nenhuma chave configurada (fallback, sem erro).
//   isLoaded true    → window.google.maps pronto pra uso.
//   loadError !null  → chave inválida / falha de rede (o consumidor cai no
//                      fallback e/ou mostra aviso).
export default function useGoogleMaps() {
   const [isLoaded, setIsLoaded] = useState(
      Boolean(window.google && window.google.maps)
   );
   const [loadError, setLoadError] = useState(null);

   useEffect(() => {
      if (!API_KEY || isLoaded) return undefined;
      let cancelled = false;
      loadGoogleMaps()
         .then(() => {
            if (!cancelled) setIsLoaded(true);
         })
         .catch((err) => {
            if (!cancelled && err?.message !== 'no-api-key') {
               setLoadError(err);
            }
         });
      return () => {
         cancelled = true;
      };
   }, [isLoaded]);

   return { apiKey: API_KEY, isLoaded, loadError };
}
